import { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import InternalNotes from "@/components/InternalNotes";
import {
  Search, Send, Sparkles, Bot, Zap, Clock, Flame,
  MessageCircle, TrendingUp, Target, FileText, User as UserIcon,
  Phone, Tag, BarChart3, ChevronRight, ArrowLeft, PanelRightOpen, X,
} from "lucide-react";
import { toast } from "sonner";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */
function relativeTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `hace ${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

const STAGE_META = {
  nuevo:       { label: "Nuevo",       bg: "rgba(59,130,246,0.12)",  text: "#60A5FA" },
  contactado:  { label: "Contactado",  bg: "rgba(168,85,247,0.12)",  text: "#C084FC" },
  calificado:  { label: "Calificado",  bg: "rgba(94,106,210,0.15)",  text: "#9EA9FF" },
  negociacion: { label: "Negociacion", bg: "rgba(245,158,11,0.12)",  text: "#FBBF24" },
  ganado:      { label: "Ganado",      bg: "rgba(16,185,129,0.12)",  text: "#34D399" },
  perdido:     { label: "Perdido",     bg: "rgba(239,68,68,0.12)",   text: "#F87171" },
};

function intentTone(score) {
  if (score == null) return { label: "Sin analizar", color: "#71717A", bg: "rgba(113,113,122,0.1)", grad: "from-zinc-700 to-zinc-700" };
  if (score >= 75)   return { label: "Alta intencion de compra", color: "#10B981", bg: "rgba(16,185,129,0.12)", grad: "from-emerald-500 to-emerald-400" };
  if (score >= 50)   return { label: "Intencion media",          color: "#F59E0B", bg: "rgba(245,158,11,0.12)", grad: "from-amber-500 to-amber-400" };
  if (score >= 25)   return { label: "Intencion baja",           color: "#FB923C", bg: "rgba(251,146,60,0.12)", grad: "from-orange-500 to-orange-400" };
  return                    { label: "Sin intencion clara",      color: "#EF4444", bg: "rgba(239,68,68,0.12)",  grad: "from-rose-500 to-rose-400" };
}

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */
export default function Inbox() {
  const { t } = useI18n();
  const [convos, setConvos]       = useState([]);
  const [selected, setSelected]   = useState(null);
  const [messages, setMessages]   = useState([]);
  const [text, setText]           = useState("");
  const [search, setSearch]       = useState("");
  const [sortBy, setSortBy]       = useState("recent");
  const [sending, setSending]     = useState(false);
  const [aiThinking, setAiThinking] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [analyzing, setAnalyzing]   = useState(false);
  const [analysis, setAnalysis]     = useState(null);
  // Mobile navigation: 'list' | 'chat' | 'insights' (overridden by CSS on md+)
  const [mobileView, setMobileView] = useState("list");
  const [params] = useSearchParams();
  const endRef = useRef(null);

  /* --- Data loaders --- */
  const loadConvos = async () => {
    const { data } = await api.get("/conversations", { params: { sort_by: sortBy } });
    setConvos(data);
    if (!selected && data[0]) setSelected(data[0].contact);
  };

  const loadMessages = async (id) => {
    const { data } = await api.get(`/messages/${id}`);
    setMessages(data);
  };

  const loadAnalysis = async (id) => {
    try {
      const { data: c } = await api.get(`/contacts/${id}`);
      setAnalysis(c.ai_analysis || null);
    } catch { setAnalysis(null); }
  };

  useEffect(() => {
    loadConvos();
    const t = setInterval(loadConvos, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  useEffect(() => {
    const id = params.get("contact");
    if (id && convos.length) {
      const found = convos.find((c) => c.contact.id === id);
      if (found) { setSelected(found.contact); setMobileView("chat"); }
    }
  }, [params, convos]);

  useEffect(() => {
    if (!selected) return;
    loadMessages(selected.id);
    loadAnalysis(selected.id);
  }, [selected]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  /* --- Actions --- */
  const send = async () => {
    if (!text.trim() || !selected) return;
    setSending(true);
    try {
      await api.post("/messages", { contact_id: selected.id, text: text.trim(), sender: "agent" });
      setText("");
      await loadMessages(selected.id);
      await loadConvos();
    } catch { toast.error("Error al enviar"); }
    finally { setSending(false); }
  };

  const aiSuggest = async () => {
    if (!selected) return;
    setAiThinking(true);
    try {
      const { data } = await api.post("/ai/reply", { contact_id: selected.id });
      setText(data.text);
      toast.success("Sugerencia IA generada");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error IA"); }
    finally { setAiThinking(false); }
  };

  const simulateIncoming = async () => {
    if (!selected) return;
    const txt = prompt("Mensaje entrante del cliente (simulado):", "Hola, quiero mas info");
    if (!txt) return;
    setSimulating(true);
    try {
      await api.post("/whatsapp/simulate-incoming", { contact_id: selected.id, text: txt, auto_reply: true });
      await loadMessages(selected.id);
      await loadConvos();
      toast.success("Mensaje simulado · IA respondio");
    } catch { toast.error("Error al simular"); }
    finally { setSimulating(false); }
  };

  const analyzeNow = async () => {
    if (!selected) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post("/ai/analyze", { contact_id: selected.id });
      setAnalysis(data);
      await loadConvos();
      toast.success("Analisis IA actualizado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setAnalyzing(false); }
  };

  /* --- Derived --- */
  const filteredConvos = useMemo(() => {
    if (!search) return convos;
    const q = search.toLowerCase();
    return convos.filter((c) =>
      c.contact.name.toLowerCase().includes(q) ||
      c.contact.phone.includes(q) ||
      (c.last_message?.text || "").toLowerCase().includes(q)
    );
  }, [convos, search]);

  const score = selected ? convos.find((c) => c.contact.id === selected.id)?.ai_score ?? selected.ai_score : null;
  const intent = intentTone(score);
  const stageMeta = selected ? STAGE_META[selected.stage] || STAGE_META.nuevo : null;

  /* ================================================================ */
  /* Render                                                           */
  /* ================================================================ */
  return (
    <div className="flex flex-col md:flex-row h-[100dvh] bg-[#09090B] text-[#FAFAFA] relative overflow-hidden" data-testid="inbox-page">

      {/* ============================================================ */}
      {/* PANEL 1: Chat list                                           */}
      {/* ============================================================ */}
      <aside
        data-testid="inbox-panel-list"
        className={`bg-[#09090B] flex-col
          md:flex md:w-[340px] md:shrink-0 md:border-r md:border-[#27272A]
          ${mobileView === "list" ? "flex flex-1 min-h-0" : "hidden"}`}
      >
        <div className="p-5 border-b border-[#27272A]">
          {/* pad left on mobile so title doesn't collide with hamburger button */}
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1 pl-14 md:pl-0">{t("inbox_kicker")}</div>
          <h2 className="font-display text-2xl font-medium tracking-tight pl-14 md:pl-0">{t("inbox_title")}</h2>

          <div className="relative mt-4">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("inbox_search")}
              data-testid="inbox-search-input"
              className="pl-9 h-9 bg-[#121214] border-[#27272A] focus:border-[#5E6AD2] focus:ring-1 focus:ring-[#5E6AD2] text-sm"
            />
          </div>

          <div className="flex items-center justify-between mt-3">
            <div className="text-[11px] text-[#71717A] font-mono">{filteredConvos.length} chats</div>
            <div className="flex gap-1 bg-[#0A0A0C] border border-[#27272A] rounded-md p-0.5">
              <button
                onClick={() => setSortBy("recent")}
                data-testid="sort-recent-button"
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] uppercase tracking-wider transition-colors ${sortBy === "recent" ? "bg-[#18181B] text-white" : "text-[#71717A] hover:text-white"}`}
              >
                <Clock size={10} /> Recientes
              </button>
              <button
                onClick={() => setSortBy("score")}
                data-testid="sort-score-button"
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] uppercase tracking-wider transition-colors ${sortBy === "score" ? "bg-[#18181B] text-white" : "text-[#71717A] hover:text-white"}`}
              >
                <Flame size={10} className={sortBy === "score" ? "fill-current" : ""} /> Hot leads
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredConvos.length === 0 && (
            <div className="p-8 text-center text-sm text-[#71717A]">
              <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
              {search ? "-" : t("inbox_empty")}
            </div>
          )}

          {filteredConvos.map((c) => {
            const active = selected?.id === c.contact.id;
            const isAITending = c.last_message?.sender === "ai"
              && c.last_at && (Date.now() - new Date(c.last_at).getTime()) < 10 * 60 * 1000;
            return (
              <button
                key={c.contact.id}
                onClick={() => { setSelected(c.contact); setMobileView("chat"); }}
                data-testid={`convo-${c.contact.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#18181B] flex items-start gap-3 transition-colors ${
                  active ? "bg-[#18181B]" : "hover:bg-[#121214]"
                }`}
              >
                <div className="relative shrink-0">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#5E6AD2] to-[#3B82F6] flex items-center justify-center text-sm font-medium">
                    {c.contact.name[0]?.toUpperCase()}
                  </div>
                  {c.unread_count > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-[#5E6AD2] text-[9px] font-mono font-medium flex items-center justify-center text-white">
                      {c.unread_count > 9 ? "9+" : c.unread_count}
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{c.contact.name}</span>
                    <span className="text-[10px] text-[#71717A] font-mono shrink-0">{relativeTime(c.last_at)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {c.last_message?.sender === "ai" && <Bot size={11} className="text-[#9EA9FF] shrink-0" />}
                    <span className="text-xs text-[#71717A] truncate flex-1">{c.last_message?.text || "Sin mensajes"}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    {isAITending && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider bg-[rgba(94,106,210,0.15)] text-[#9EA9FF]">
                        <span className="h-1 w-1 rounded-full bg-[#9EA9FF] dot-pulse" />
                        Atendiendo IA
                      </span>
                    )}
                    {c.ai_score != null && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono font-medium" style={{ background: intentTone(c.ai_score).bg, color: intentTone(c.ai_score).color }}>
                        <Sparkles size={8} /> {c.ai_score}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* ============================================================ */}
      {/* PANEL 2: Conversation                                        */}
      {/* ============================================================ */}
      <section
        data-testid="inbox-panel-chat"
        className={`bg-[#09090B] flex-col min-w-0 min-h-0
          md:flex md:flex-1
          ${mobileView === "chat" ? "flex flex-1" : "hidden"}`}
      >
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-[#71717A] text-sm">
            <div className="text-center">
              <MessageCircle size={48} className="mx-auto mb-3 text-[#27272A]" strokeWidth={1.2} />
              <div>Selecciona una conversacion</div>
            </div>
          </div>
        ) : (
          <>
            {/* Chat header — pl-14 md:pl-0 so mobile hamburger doesn't overlap avatar */}
            <header className="pl-14 md:pl-6 pr-4 md:pr-6 py-3 md:py-4 border-b border-[#27272A] flex items-center justify-between gap-2">
              {/* Back to list (mobile only) */}
              <button
                onClick={() => setMobileView("list")}
                data-testid="back-to-list-button"
                className="md:hidden p-2 -ml-2 rounded hover:bg-[#18181B] text-[#A1A1AA] shrink-0"
                aria-label="Volver a la lista"
              >
                <ArrowLeft size={18} />
              </button>

              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#5E6AD2] to-[#3B82F6] flex items-center justify-center text-sm font-medium shrink-0">
                  {selected.name[0]?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <Link to={`/app/contacts/${selected.id}`} className="text-sm font-medium hover:text-[#9EA9FF] truncate block" data-testid="open-contact-detail">
                    {selected.name}
                  </Link>
                  <div className="text-xs text-[#71717A] font-mono flex items-center gap-1 truncate">
                    <Phone size={10} className="shrink-0" /> {selected.phone}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm" variant="ghost"
                  onClick={simulateIncoming} disabled={simulating}
                  data-testid="simulate-incoming-button"
                  className="text-xs text-[#A1A1AA] hover:text-white hover:bg-[#18181B] hidden sm:inline-flex"
                >
                  <Zap size={14} className="mr-1.5" /> {simulating ? "..." : "Simular"}
                </Button>
                {/* Insights toggle (mobile only) */}
                <button
                  onClick={() => setMobileView("insights")}
                  data-testid="open-insights-button"
                  className="md:hidden p-2 rounded hover:bg-[#18181B] text-[#A1A1AA] inline-flex items-center gap-1"
                  aria-label="Ver insights"
                >
                  <PanelRightOpen size={18} />
                  <Sparkles size={11} className="text-[#9EA9FF] fill-current -ml-0.5" />
                </button>
              </div>
            </header>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6 space-y-4 bg-[#09090B]">
              {messages.length === 0 && (
                <div className="text-center text-sm text-[#71717A] mt-12">
                  Sin mensajes aun. Usa "Simular entrante" para probar el flujo IA.
                </div>
              )}
              {messages.map((m) => {
                const isCustomer = m.direction === "inbound";
                const isAI = m.sender === "ai";
                return (
                  <div key={m.id} className={`flex ${isCustomer ? "justify-start" : "justify-end"} fade-in`}>
                    <div className={`max-w-[85%] sm:max-w-[68%] ${isCustomer ? "" : "flex flex-col items-end"}`}>
                      <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        isCustomer
                          ? "bg-[#18181B] text-white border border-[#27272A] rounded-tl-sm"
                          : isAI
                            ? "bg-gradient-to-br from-[#1A1B3A] to-[#221d3a] text-white border border-[rgba(94,106,210,0.45)] rounded-tr-sm ai-glow"
                            : "bg-[#5E6AD2] text-white rounded-tr-sm"
                      }`}>
                        {m.text}
                      </div>
                      <div className={`text-[10px] text-[#71717A] mt-1 font-mono flex items-center gap-1.5 px-1 ${isCustomer ? "" : "flex-row-reverse"}`}>
                        {isAI && (
                          <span className="inline-flex items-center gap-1 text-[#9EA9FF]">
                            <Sparkles size={10} className="fill-current" /> IA
                          </span>
                        )}
                        {!isAI && !isCustomer && (
                          <span className="inline-flex items-center gap-1 text-[#A1A1AA]">
                            <UserIcon size={10} /> Agente
                          </span>
                        )}
                        <span>{new Date(m.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</span>
                        {!isCustomer && m.status === "failed" && <span className="text-[#EF4444]">fallido</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>

            {/* Input */}
            <div className="border-t border-[#27272A] p-3 md:p-4 bg-[#0E0E10] safe-bottom">
              <div className="flex gap-2 mb-2">
                <Button
                  size="sm" variant="ghost"
                  onClick={aiSuggest} disabled={aiThinking}
                  data-testid="ai-suggest-button"
                  className="text-xs bg-[#1A1B3A] hover:bg-[#252762] text-[#9EA9FF] border border-[rgba(94,106,210,0.4)]"
                >
                  <Sparkles size={14} className="mr-1.5 fill-current" />
                  {aiThinking ? "Pensando..." : "Sugerir con IA"}
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Escribe un mensaje..."
                  data-testid="message-input"
                  className="bg-[#121214] border-[#27272A] focus:border-[#5E6AD2] focus:ring-1 focus:ring-[#5E6AD2]"
                />
                <Button
                  onClick={send} disabled={sending || !text.trim()}
                  data-testid="send-message-button"
                  className="bg-[#5E6AD2] hover:bg-[#4B55C3] shrink-0"
                >
                  <Send size={16} />
                </Button>
              </div>
            </div>
          </>
        )}
      </section>

      {/* ============================================================ */}
      {/* PANEL 3: AI Insights + CRM                                   */}
      {/* ============================================================ */}
      <aside
        data-testid="inbox-insights-panel"
        className={`bg-[#0B0B0D] overflow-y-auto
          md:flex md:w-[360px] md:shrink-0 md:border-l md:border-[#27272A]
          ${mobileView === "insights" ? "flex flex-col fixed inset-0 z-40" : "hidden"}`}
      >
        {!selected ? (
          <div className="p-6 text-sm text-[#71717A]">Selecciona un contacto para ver insights.</div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Mobile-only header with close X */}
            <div className="md:hidden flex items-center justify-between -mt-1 mb-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Insights IA</div>
              <button
                onClick={() => setMobileView("chat")}
                data-testid="close-insights-button"
                className="p-1.5 rounded hover:bg-[#18181B] text-[#A1A1AA]"
                aria-label="Cerrar insights"
              >
                <X size={18} />
              </button>
            </div>

            {/* Contact mini-card */}
            <div className="flex items-center gap-3 pb-4 border-b border-[#27272A]">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#5E6AD2] to-[#3B82F6] flex items-center justify-center text-base font-medium">
                {selected.name[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{selected.name}</div>
                <div className="text-[11px] text-[#71717A] font-mono truncate">{selected.phone}</div>
              </div>
              <Link to={`/app/contacts/${selected.id}`} className="text-[#71717A] hover:text-white" data-testid="goto-contact">
                <ChevronRight size={16} />
              </Link>
            </div>

            {/* Estado del lead */}
            <div className="bg-[#121214] border border-[#27272A] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target size={12} className="text-[#71717A]" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Estado del lead</span>
              </div>
              {stageMeta && (
                <div
                  className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium uppercase tracking-wider"
                  style={{ background: stageMeta.bg, color: stageMeta.text }}
                  data-testid="insight-stage"
                >
                  {stageMeta.label}
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-3">
                {(selected.tags || []).map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-[#18181B] border border-[#27272A] text-[#A1A1AA]">
                    <Tag size={9} /> {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Termometro IA */}
            <div className="bg-[#121214] border border-[#27272A] rounded-lg p-4" data-testid="insight-thermometer">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <TrendingUp size={12} className="text-[#71717A]" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Termometro IA</span>
                </div>
                <Button
                  size="sm" variant="ghost"
                  onClick={analyzeNow} disabled={analyzing || messages.length === 0}
                  data-testid="analyze-now-button"
                  className="h-6 px-2 text-[10px] text-[#9EA9FF] hover:bg-[#1A1B3A]"
                >
                  <Sparkles size={10} className="mr-1 fill-current" />
                  {analyzing ? "..." : analysis ? "Re-analizar" : "Analizar"}
                </Button>
              </div>

              {score == null ? (
                <div className="py-6 text-center text-xs text-[#71717A]">
                  {messages.length === 0 ? "Sin mensajes para analizar" : "Sin analisis aun. Haz clic en Analizar."}
                </div>
              ) : (
                <>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="font-display text-4xl font-light tracking-tighter text-white">{score}</span>
                    <span className="text-xs text-[#71717A]">/ 100</span>
                    <span className="ml-auto text-[10px] uppercase tracking-wider font-medium" style={{ color: intent.color }}>
                      Probabilidad de cierre
                    </span>
                  </div>
                  <div className="h-2 bg-[#18181B] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full bg-gradient-to-r ${intent.grad} transition-all duration-500`}
                      style={{ width: `${score}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[9px] text-[#52525B] mt-1 font-mono">
                    <span>0</span><span>50</span><span>100</span>
                  </div>
                </>
              )}
            </div>

            {/* Intencion detectada */}
            <div className="bg-[#121214] border border-[#27272A] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Flame size={12} className="text-[#71717A]" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Intencion detectada</span>
              </div>
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium"
                style={{ background: intent.bg, color: intent.color }}
                data-testid="insight-intent"
              >
                <Sparkles size={12} className="fill-current" />
                {intent.label}
              </div>
              {analysis?.sentiment && (
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="text-[#71717A] uppercase tracking-wider text-[10px]">Sentimiento:</span>
                  <span className="capitalize text-[#FAFAFA]">{analysis.sentiment}</span>
                </div>
              )}
            </div>

            {/* Resumen IA */}
            <div className="bg-[#121214] border border-[#27272A] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText size={12} className="text-[#71717A]" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Resumen IA</span>
              </div>
              {analysis?.summary ? (
                <p className="text-sm text-[#E4E4E7] leading-relaxed" data-testid="insight-summary">{analysis.summary}</p>
              ) : (
                <p className="text-xs text-[#71717A] italic">Aun no hay resumen. Analiza la conversacion para generarlo.</p>
              )}
              {analysis?.next_action && (
                <div className="mt-3 pt-3 border-t border-[#27272A]">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Siguiente accion</div>
                  <p className="text-xs text-[#9EA9FF] leading-relaxed">{analysis.next_action}</p>
                </div>
              )}
              {analysis?.topics?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#27272A]">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Temas</div>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.topics.map((t, i) => (
                      <span key={i} className="px-2 py-0.5 text-[10px] rounded bg-[rgba(94,106,210,0.12)] text-[#9EA9FF] border border-[rgba(94,106,210,0.25)]">{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Stats footer */}
            <div className="bg-[#121214] border border-[#27272A] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 size={12} className="text-[#71717A]" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Actividad</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div>
                  <div className="font-display text-2xl font-light text-white">{messages.length}</div>
                  <div className="text-[10px] text-[#71717A] uppercase tracking-wider mt-0.5">Mensajes</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-light text-[#9EA9FF]">{messages.filter((m) => m.sender === "ai").length}</div>
                  <div className="text-[10px] text-[#71717A] uppercase tracking-wider mt-0.5">via IA</div>
                </div>
              </div>
            </div>

            {selected && <InternalNotes contactId={selected.id} />}

          </div>
        )}
      </aside>
    </div>
  );
}
